import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { theme, alpha } from '../../../theme';
import { CreateManualBookingRequest, GridVenue, BookingChannel, CHANNEL_LABELS, CHANNEL_COLORS } from '../../../types/venue';
import { User, Phone, FileText, ChevronRight, DollarSign } from 'lucide-react';

interface QuickCreatePopoverProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateManualBookingRequest) => Promise<{ success: boolean; error?: string }>;
  onOpenFullModal: (partialData: { customerName: string; customerPhone: string; notes: string; bookingChannel?: BookingChannel }) => void;
  anchorRect: DOMRect | null;
  venueId: string;
  venueName: string;
  date: string;
  time: string;
  depositAmount?: number;
}

const POPOVER_WIDTH = 320;

const QuickCreatePopover: React.FC<QuickCreatePopoverProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onOpenFullModal,
  anchorRect,
  venueId,
  venueName,
  date,
  time,
  depositAmount,
}) => {
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [bookingChannel, setBookingChannel] = useState<BookingChannel>('WHATSAPP');
  const [depositMarkedPaid, setDepositMarkedPaid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const popoverRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Reset form on open/close
  useEffect(() => {
    if (isOpen) {
      setCustomerName('');
      setCustomerPhone('');
      setNotes('');
      setBookingChannel('WHATSAPP');
      setDepositMarkedPaid(false);
      setError(null);
      setLoading(false);
      // Auto-focus after portal renders
      requestAnimationFrame(() => {
        nameInputRef.current?.focus();
      });
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    // Use setTimeout so the opening click doesn't immediately close
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Close on scroll in the grid container
  // Grace period of 200ms ignores micro-scrolls triggered by portal mount / reflow
  useEffect(() => {
    if (!isOpen) return;
    let active = false;
    let debounceTimer: NodeJS.Timeout;
    const handleScroll = () => {
      if (!active) return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => onClose(), 80);
    };
    document.addEventListener('scroll', handleScroll, true);
    const activateTimer = setTimeout(() => {
      active = true;
    }, 200);
    return () => {
      clearTimeout(activateTimer);
      clearTimeout(debounceTimer);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, onClose]);

  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!customerName.trim()) {
      setError('Ingrese el nombre del cliente');
      nameInputRef.current?.focus();
      return;
    }

    setError(null);
    setLoading(true);

    const [hours, minutes] = time.split(':').map(Number);
    const endHours = hours + 1;
    const endTime = `${endHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

    const result = await onSubmit({
      venueId,
      bookingDate: date,
      startTime: time,
      endTime,
      customerName: customerName.trim(),
      customerPhone: customerPhone.trim() || undefined,
      depositPaid: depositMarkedPaid && depositAmount ? depositAmount : undefined,
      notes: notes.trim() || undefined,
      bookingChannel,
    });

    setLoading(false);

    if (result.success) {
      onClose();
    } else {
      setError(result.error || 'Error al crear reserva');
    }
  }, [customerName, customerPhone, notes, bookingChannel, depositMarkedPaid, depositAmount, venueId, date, time, onSubmit, onClose]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleOpenFull = () => {
    onOpenFullModal({
      customerName: customerName.trim(),
      customerPhone: customerPhone.trim(),
      notes: notes.trim(),
      bookingChannel,
    });
  };

  if (!isOpen || !anchorRect) return null;

  // Format date for header
  const dateObj = new Date(date + 'T00:00:00');
  const dayNames = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'];
  const dayName = dayNames[dateObj.getDay()];
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const [h, m] = time.split(':').map(Number);
  const endH = h + 1;
  const endTimeStr = `${endH.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  const headerText = `${venueName} - ${dayName} ${day}/${month} - ${time} a ${endTimeStr}`;

  // Calculate position
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;

  let left = anchorRect.left + anchorRect.width / 2 - POPOVER_WIDTH / 2;
  // Clamp horizontal
  left = Math.max(8, Math.min(left, viewportW - POPOVER_WIDTH - 8));

  // Prefer below the cell, fallback above
  const spaceBelow = viewportH - anchorRect.bottom;
  const popoverEstimatedHeight = 360;
  let top: number;
  if (spaceBelow >= popoverEstimatedHeight + 8) {
    top = anchorRect.bottom + 4;
  } else {
    top = anchorRect.top - popoverEstimatedHeight - 4;
    if (top < 8) top = 8;
  }

  const popoverStyle: React.CSSProperties = {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
    width: `${POPOVER_WIDTH}px`,
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    border: `1px solid ${theme.colors.border}`,
    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
    zIndex: 1000,
    overflow: 'hidden',
  };

  const headerStyle: React.CSSProperties = {
    padding: '12px 16px',
    borderBottom: `1px solid ${theme.colors.border}`,
    fontSize: '13px',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    backgroundColor: `${alpha('primary', '15')}`,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  const bodyStyle: React.CSSProperties = {
    padding: '12px 16px',
  };

  const fieldStyle: React.CSSProperties = {
    marginBottom: '10px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.textSecondary,
    marginBottom: '4px',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: theme.borderRadius.input,
    border: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const errorStyle: React.CSSProperties = {
    backgroundColor: `${alpha('danger', '20')}`,
    color: theme.colors.danger,
    padding: '8px 12px',
    borderRadius: theme.borderRadius.input,
    fontSize: '12px',
    marginBottom: '10px',
  };

  const footerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 16px',
    borderTop: `1px solid ${theme.colors.border}`,
  };

  const linkStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    color: theme.colors.textSecondary,
    fontSize: '13px',
    cursor: 'pointer',
    padding: '4px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  };

  const submitBtnStyle: React.CSSProperties = {
    padding: '8px 20px',
    borderRadius: theme.borderRadius.button,
    fontSize: '14px',
    fontWeight: theme.fonts.weightBold,
    cursor: loading ? 'default' : 'pointer',
    border: 'none',
    backgroundColor: theme.colors.primary,
    color: '#000',
    opacity: loading ? 0.7 : 1,
  };

  return createPortal(
    <div ref={popoverRef} style={popoverStyle}>
      {/* Header */}
      <div style={headerStyle}>{headerText}</div>

      {/* Body */}
      <form onSubmit={handleSubmit} style={bodyStyle}>
        {error && <div style={errorStyle}>{error}</div>}

        <div style={fieldStyle}>
          <label style={labelStyle}>
            <User size={14} />
            Nombre *
          </label>
          <input
            ref={nameInputRef}
            type="text"
            style={inputStyle}
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Nombre del cliente"
          />
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>
            <Phone size={14} />
            Telefono
          </label>
          <input
            type="tel"
            style={inputStyle}
            value={customerPhone}
            onChange={(e) => setCustomerPhone(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="+54 11 1234-5678"
          />
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>
            <FileText size={14} />
            Nota
          </label>
          <input
            type="text"
            style={inputStyle}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Nota rapida (opcional)"
          />
        </div>

        <div style={{ marginBottom: '10px' }}>
          <label style={labelStyle}>Canal</label>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {(['WHATSAPP', 'PHONE', 'WALKIN', 'INSTAGRAM', 'OTHER'] as BookingChannel[]).map((ch) => (
              <button
                key={ch}
                type="button"
                onClick={() => setBookingChannel(ch)}
                style={{
                  padding: '4px 10px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: bookingChannel === ch ? 700 : 400,
                  cursor: 'pointer',
                  border: bookingChannel === ch
                    ? `2px solid ${CHANNEL_COLORS[ch]}`
                    : `1px solid ${theme.colors.border}`,
                  backgroundColor: bookingChannel === ch ? `${CHANNEL_COLORS[ch]}20` : 'transparent',
                  color: bookingChannel === ch ? CHANNEL_COLORS[ch] : theme.colors.textSecondary,
                  transition: 'all 0.15s',
                }}
              >
                {CHANNEL_LABELS[ch]}
              </button>
            ))}
          </div>
        </div>

        {depositAmount != null && depositAmount > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <button
              type="button"
              onClick={() => setDepositMarkedPaid(!depositMarkedPaid)}
              style={{
                width: '36px',
                height: '20px',
                borderRadius: '10px',
                border: 'none',
                backgroundColor: depositMarkedPaid ? theme.colors.primary : theme.colors.border,
                position: 'relative',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                padding: 0,
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: '2px',
                  left: depositMarkedPaid ? '18px' : '2px',
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  transition: 'left 0.2s',
                }}
              />
            </button>
            <span style={{ fontSize: '12px', color: theme.colors.textSecondary, display: 'flex', alignItems: 'center', gap: '4px' }}>
              <DollarSign size={12} />
              Sena abonada (${depositAmount})
            </span>
          </div>
        )}
      </form>

      {/* Footer */}
      <div style={footerStyle}>
        <button type="button" style={linkStyle} onClick={handleOpenFull}>
          Mas opciones <ChevronRight size={14} />
        </button>
        <button
          type="button"
          style={submitBtnStyle}
          onClick={() => handleSubmit()}
          disabled={loading}
        >
          {loading ? 'Guardando...' : 'Guardar'}
        </button>
      </div>
    </div>,
    document.body
  );
};

export default QuickCreatePopover;
